import { useCountries, CountryTag } from "@/components/Country";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Upload, Sparkles, Layers, Search, Code2, Pencil, Trash2, Plus } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { TownMap } from "@/components/TownMap";
import { STATUS } from "@/components/townStatus";
import { RegionProgress } from "@/components/RegionProgress";
import { TownEdit } from "@/components/TownEdit";
import { useTownTrash, UndoBar } from "@/components/TownUndo";
import { TrimRegion } from "@/components/TrimRegion";
import { TownNote } from "@/components/TownNote";

const PAGE = 50;
const sel = "h-10 bg-white border border-input px-3 text-sm rounded-none";

const ImportButton = ({ onDone }) => {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/towns/import", fd);
      toast.success(`${data.added} towns added${data.updated ? `, ${data.updated} updated` : ""}${data.skipped ? `, ${data.skipped} unchanged` : ""}`);
      if (data.unknown_countries?.length) toast.warning(`Skipped rows for countries not set up yet: ${data.unknown_countries.join(", ")}`);
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); ref.current.value = ""; }
  };
  return (
    <>
      <input ref={ref} type="file" accept=".xlsx,.xlsm" className="hidden" data-testid="towns-import-input" onChange={(e) => upload(e.target.files[0])} />
      <Button variant="outline" data-testid="towns-import-button" onClick={() => ref.current.click()} disabled={busy} className="rounded-none"><Upload className="w-4 h-4 mr-2" />{busy ? "Importing…" : "Import Excel"}</Button>
    </>
  );
};

const ManualStatus = ({ t, onChange }) => {
  if (t.guide_id) return <Link to={`/guides/${t.guide_id}`} data-testid={`town-open-${t.id}`} className="text-xs text-[#3B1B48] underline">No. {t.issue_number}</Link>;
  const set = async (v) => {
    try { await api.patch(`/towns/${t.id}`, { manual_status: v || null }); onChange(t.id, v || null); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <select data-testid={`town-status-select-${t.id}`} value={t.manual_status || ""} onChange={(e) => set(e.target.value)} className="h-8 bg-white border border-input px-2 text-xs rounded-none">
      <option value="">—</option><option value="shortlisted">Shortlist</option><option value="skip">Skip</option>
    </select>
  );
};

const Row = ({ t, checked, onCheck, onChange, onEdit, onDelete, onNote }) => {
  const st = STATUS[t.status];
  return (
    <tr data-testid={`town-row-${t.id}`} className="border-b border-[#E2D9CC] hover:bg-[#F4EFE6]/60 transition-colors">
      <td className="px-3 py-2"><input type="checkbox" data-testid={`town-check-${t.id}`} checked={checked} disabled={!!t.guide_id} onChange={() => onCheck(t.id)} className="accent-[#3B1B48]" /></td>
      <td className="px-3 py-2 text-xs text-[#8C827A] font-mono-accent">{t.number ?? "—"}</td>
      <td className="px-3 py-2 font-display text-base text-[#3B1B48]">{t.town}{t.geo === "failed" && <span className="ml-2 text-[10px] text-[#B45309]" title="Could not find on the map">no map pin</span>}<TownNote t={t} onSaved={onNote} /></td>
      <td className="px-3 py-2 text-sm text-[#4A1E5C]">{t.province}</td>
      <td className="px-3 py-2 text-sm"><span className="flex items-center gap-2">{t.region}<CountryTag country={t.country} testId={`town-country-${t.id}`} /></span></td>
      <td className="px-3 py-2"><span data-testid={`town-status-${t.id}`} className={`text-[11px] px-2 py-1 uppercase tracking-wider ${st.cls}`}>{st.label}</span></td>
      <td className="px-3 py-2"><ManualStatus t={t} onChange={onChange} /></td>
      <td className="px-3 py-2 text-right whitespace-nowrap">
        {!t.guide_id && <Link to={`/new?town=${encodeURIComponent(t.town)}&region=${encodeURIComponent(t.region)}&country=${encodeURIComponent(t.country || "Italy")}`} data-testid={`town-create-${t.id}`} className="text-xs inline-flex items-center gap-1 text-[#3B1B48] hover:text-[#C59B27]"><Sparkles className="w-3.5 h-3.5" />Create guide</Link>}
        <button data-testid={`town-edit-${t.id}`} onClick={() => onEdit(t)} title="Edit town" className="ml-3 p-1 text-[#8C827A] hover:text-[#3B1B48] transition-colors"><Pencil className="w-3.5 h-3.5" /></button>
        <button data-testid={`town-delete-${t.id}`} onClick={() => onDelete(t)} disabled={!!t.guide_id} title={t.guide_id ? "Has a guide, so it can't be deleted" : "Delete town"} className="p-1 text-[#8C827A] hover:text-red-700 transition-colors disabled:opacity-30 disabled:hover:text-[#8C827A]"><Trash2 className="w-3.5 h-3.5" /></button>
      </td>
    </tr>
  );
};

const EmbedInfo = ({ country }) => {
  const url = `${process.env.REACT_APP_BACKEND_URL}/api/public/map/embed${country ? `?country=${encodeURIComponent(country)}` : ""}`;
  const code = `<iframe src="${url}" width="100%" height="520" style="border:0" loading="lazy" title="Small Towns${country ? ` of ${country}` : ""} map"></iframe>`;
  return (
    <details className="mt-3 text-sm" data-testid="map-embed-info">
      <summary className="cursor-pointer text-[#3B1B48] inline-flex items-center gap-2"><Code2 className="w-4 h-4" />Embed the public map on theexpatstudio.com (published guides only{country ? `, ${country}` : ", all countries"})</summary>
      <div className="mt-2 flex gap-2">
        <code data-testid="map-embed-code" className="flex-1 bg-[#F4EFE6] border border-[#E2D9CC] p-3 text-xs break-all">{code}</code>
        <Button variant="outline" data-testid="map-embed-copy" onClick={() => { navigator.clipboard.writeText(code); toast.success("Embed code copied"); }} className="rounded-none">Copy</Button>
      </div>
      <p className="text-xs text-[#8C827A] mt-2">JSON for your own map: <span className="font-mono-accent">{process.env.REACT_APP_BACKEND_URL}/api/public/map</span></p>
    </details>
  );
};

export default function TownsPage() {
  const nav = useNavigate();
  const [data, setData] = useState({ towns: [], geocoding: {} });
  const [q, setQ] = useState("");
  const [params] = useSearchParams();
  const [region, setRegion] = useState(params.get("region") || "");
  const [country, setCountry] = useState(params.get("country") || "");
  const countryList = useCountries();
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(0);
  const [picked, setPicked] = useState(new Set());
  const load = () => api.get("/towns").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  useEffect(() => { if (!data.geocoding.pending) return; const t = setTimeout(load, 15000); return () => clearTimeout(t); }, [data]);
  useEffect(() => setPage(0), [q, region, status, country]);
  const prevCountry = useRef(country);
  useEffect(() => { if (prevCountry.current !== country) { prevCountry.current = country; setRegion(""); } }, [country]);

  const inCountry = useMemo(() => data.towns.filter((t) => !country || t.country === country), [data.towns, country]);
  const regionGroups = useMemo(() => {
    const g = {};
    inCountry.forEach((t) => { if (t.region) (g[t.country] = g[t.country] || new Set()).add(t.region); });
    return Object.entries(g).sort(([a], [b]) => (a === "Italy" ? -1 : b === "Italy" ? 1 : a.localeCompare(b))).map(([c, set]) => [c, [...set].sort()]);
  }, [inCountry]);
  const counts = useMemo(() => inCountry.reduce((a, t) => ({ ...a, [t.status]: (a[t.status] || 0) + 1 }), {}), [inCountry]);
  const byCountry = useMemo(() => data.towns.reduce((a, t) => ({ ...a, [t.country]: (a[t.country] || 0) + 1 }), {}), [data.towns]);
  const rows = inCountry.filter((t) => (!region || t.region === region) && (!status || t.status === status) && (!q || t.town.toLowerCase().includes(q.toLowerCase()) || (t.notes || "").toLowerCase().includes(q.toLowerCase())));
  const shown = rows.slice(page * PAGE, page * PAGE + PAGE);
  const toggle = (id) => setPicked((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const patch = (id, v) => setData((d) => ({ ...d, towns: d.towns.map((t) => (t.id === id ? { ...t, manual_status: v, status: v || "not_started" } : t)) }));
  const pickedTowns = data.towns.filter((t) => picked.has(t.id));
  const pickedCountries = [...new Set(pickedTowns.map((t) => t.country))];
  const toBatch = () => {
    if (pickedCountries.length > 1) return toast.error("Pick towns from one country per batch: each country is its own series");
    nav("/batch", { state: { country: pickedCountries[0], towns: pickedTowns.map((t) => [t.town, t.region].filter(Boolean).join(", ")) } });
  };
  const [editing, setEditing] = useState(null);
  const { batches, restore, announce } = useTownTrash(() => load());
  const delOne = async (t) => {
    if (!window.confirm(`Delete ${t.town} from the towns list?`)) return;
    try { const { data: d } = await api.delete(`/towns/${t.id}`); announce(d.batch_id, t.town); setPicked((p) => { const n = new Set(p); n.delete(t.id); return n; }); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const delPicked = async () => {
    if (!window.confirm(`Delete ${picked.size} ticked town${picked.size > 1 ? "s" : ""} from the list?`)) return;
    try {
      const { data: d } = await api.post("/towns/delete", { ids: [...picked] });
      if (d.batch_id) announce(d.batch_id, `${d.deleted} town${d.deleted === 1 ? "" : "s"}`);
      if (d.kept_with_guides.length) toast.warning(`Kept (they have guides): ${d.kept_with_guides.join(", ")}`);
      setPicked(new Set()); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="rise" data-testid="towns-page">
      <div className="flex flex-wrap items-end gap-6 mb-8">
        <div>
          <p className="eyebrow mb-3">The series · {inCountry.length} towns</p>
          <h1 className="font-display text-4xl sm:text-5xl text-[#3B1B48]">Small towns of {country || "Europe"}</h1>
          <p className="text-[#8C827A] mt-3 max-w-2xl">Plan the series here: every candidate town, your shortlist, progress by region and the map. Pick towns and send them to Batch to make guides; finished guides are tracked under Guides. Your Excel can include a Country column (e.g. Italy, France); rows without one count as Italy.</p>
        </div>
        <div className="ml-auto flex gap-2">
          <TrimRegion region={region} country={country} total={inCountry.filter((t) => t.region === region).length} announce={announce} onDone={load} />
          <Button variant="outline" data-testid="towns-add-button" onClick={() => setEditing({ country: country || "Italy", region })} className="rounded-none"><Plus className="w-4 h-4 mr-2" />Add town</Button>
          <ImportButton onDone={load} />
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mb-5" data-testid="towns-country-tabs">
        {[{ name: "" }, ...countryList].map((c) => (
          <button key={c.name || "all"} data-testid={`towns-country-${(c.name || "all").toLowerCase()}`} onClick={() => setCountry(c.name)}
            className={`text-sm px-4 py-2 border transition-colors ${country === c.name ? "border-transparent" : "border-[#E2D9CC] bg-white text-[#3B1B48] hover:border-[#3B1B48]"}`}
            style={country === c.name ? { background: c.palette?.dark || "#3B1B48", color: c.palette?.accent || "#FAF7F2" } : {}}>
            {c.name || "All countries"} · {c.name ? byCountry[c.name] || 0 : data.towns.length}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap gap-2 mb-6" data-testid="towns-status-counts">
        {Object.entries(STATUS).filter(([k]) => counts[k]).map(([k, v]) => (
          <button key={k} data-testid={`towns-count-${k}`} onClick={() => setStatus(status === k ? "" : k)} className={`text-xs px-3 py-1.5 uppercase tracking-wider transition-opacity ${v.cls} ${status && status !== k ? "opacity-40" : ""}`}>{v.label} · {counts[k]}</button>
        ))}
      </div>
      <div className="mb-10"><RegionProgress active={region} onPick={setRegion} refreshKey={data.towns.length} country={country} /></div>
      <TownMap country={country} towns={region ? inCountry.filter((t) => t.region === region) : inCountry} />
      {data.geocoding.pending > 0 && <p data-testid="towns-geocoding" className="text-xs text-[#B45309] mt-2">Placing towns on the map… {data.geocoding.pending} to go (about one a second)</p>}
      <EmbedInfo country={country} />
      <div className="flex flex-wrap items-center gap-3 mt-10 mb-4">
        <div className="relative"><Search className="w-4 h-4 absolute left-3 top-3 text-[#8C827A]" /><Input data-testid="towns-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search towns" className="pl-9 h-10 w-64 rounded-none bg-white" /></div>
        <select data-testid="towns-region-filter" value={region} onChange={(e) => setRegion(e.target.value)} className={sel}><option value="">All regions</option>{regionGroups.map(([c, rs]) => <optgroup key={c} label={c}>{rs.map((r) => <option key={`${c}-${r}`} value={r}>{r}</option>)}</optgroup>)}</select>
        <select data-testid="towns-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className={sel}><option value="">All statuses</option>{Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}</select>
        <span className="text-sm text-[#8C827A]" data-testid="towns-result-count">{rows.length} town{rows.length === 1 ? "" : "s"}</span>
        {picked.size > 0 && <div className="ml-auto flex gap-2">
          <Button variant="outline" data-testid="towns-delete-picked" onClick={delPicked} className="rounded-none text-red-700 border-red-200 hover:bg-red-50"><Trash2 className="w-4 h-4 mr-2" />Delete {picked.size}</Button>
          <Button data-testid="towns-send-to-batch" onClick={toBatch} className="rounded-none"><Layers className="w-4 h-4 mr-2" />Send {picked.size} to Batch</Button>
        </div>}
      </div>
      <UndoBar batches={batches} restore={restore} />
      <div className="border border-[#E2D9CC] bg-white overflow-x-auto">
        <table className="w-full text-left" data-testid="towns-table">
          <thead className="bg-[#F4EFE6] text-[11px] uppercase tracking-wider text-[#8C827A]">
            <tr><th className="px-3 py-2 w-8" /><th className="px-3 py-2">#</th><th className="px-3 py-2">Town</th><th className="px-3 py-2">Province</th><th className="px-3 py-2">Region</th><th className="px-3 py-2">Status</th><th className="px-3 py-2">Plan</th><th className="px-3 py-2" /></tr>
          </thead>
          <tbody>{shown.map((t) => <Row key={t.id} t={t} checked={picked.has(t.id)} onCheck={toggle} onChange={patch} onEdit={setEditing} onDelete={delOne} onNote={(id, notes) => setData((d) => ({ ...d, towns: d.towns.map((x) => (x.id === id ? { ...x, notes } : x)) }))} />)}</tbody>
        </table>
        {!data.towns.length && <p className="p-8 text-center text-[#8C827A]" data-testid="towns-empty">No towns yet. Import your Excel list to get started.</p>}
      </div>
      {rows.length > PAGE && (
        <div className="flex items-center gap-3 mt-4 text-sm">
          <Button variant="outline" data-testid="towns-prev-page" disabled={!page} onClick={() => setPage(page - 1)} className="rounded-none h-8">Previous</Button>
          <span data-testid="towns-page-label">Page {page + 1} of {Math.ceil(rows.length / PAGE)}</span>
          <Button variant="outline" data-testid="towns-next-page" disabled={(page + 1) * PAGE >= rows.length} onClick={() => setPage(page + 1)} className="rounded-none h-8">Next</Button>
        </div>
      )}
      <TownEdit town={editing} countries={countryList} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
    </div>
  );
}
